/**
 * VRG-665 — the pure half of the Affiliates tab: the API range, the stage list, step rates
 * and money. NO IMPORTS on purpose, so `test/affiliates-dashboard.guard.spec.ts` loads it
 * under node's own runner (type-stripped) and pins the rules below.
 */

/** The page's inclusive UTC day range → the API's `from` inclusive / `to` exclusive. */
export const toApiRange = (range: { since: string; until: string }): { from: string; to: string } => {
  const end = new Date(`${range.until}T00:00:00.000Z`)
  end.setUTCDate(end.getUTCDate() + 1)
  return { from: range.since, to: end.toISOString().slice(0, 10) }
}

export interface StageInput {
  sent: number
  signed_up: number
  granted: number
  company_created: number
  became_client: number
  paying: number
  capped_or_ended: number
}

export type StageKey = keyof StageInput

/** Labels say what a stage MEANS for the prospect, not the internal status name. */
export const STAGE_LABELS: Record<StageKey, string> = {
  sent: 'Sent',
  signed_up: 'Signed up',
  granted: 'Granted access',
  company_created: 'Company created',
  became_client: 'Became a client',
  paying: 'Paying',
  capped_or_ended: 'Capped or ended',
}

const ORDER: StageKey[] = [
  'sent',
  'signed_up',
  'granted',
  'company_created',
  'became_client',
  'paying',
  'capped_or_ended',
]

/** Undefined (null), not 0 or 100%, when the stage before is zero: 0 of 0 is not a rate. */
export const stepRate = (prev: number, cur: number): number | null => (prev > 0 ? cur / prev : null)

export interface Stage {
  key: StageKey
  label: string
  count: number
  /** Share of the stage before, 0..1; null for the first stage or over a zero. */
  rate: number | null
}

/**
 * The funnel in order, each stage with its step rate. Counts are cumulative ("reached this
 * stage or a later one") on the API side, so a stage never exceeds the one before it.
 */
export const buildStages = (f: StageInput): Stage[] =>
  ORDER.map((key, i) => ({
    key,
    label: STAGE_LABELS[key],
    count: f[key],
    rate: i === 0 ? null : stepRate(f[ORDER[i - 1]], f[key]),
  }))

/**
 * FirstPromoter amounts arrive in cents (see `firstpromoter-reporting.client.ts` on the API
 * for the one unconfirmed assumption). Whole dollars once over $1,000; cents below.
 */
export const formatUsdCents = (cents: number): string => {
  const dollars = cents / 100
  return dollars.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: Math.abs(dollars) >= 1000 ? 0 : 2,
    minimumFractionDigits: Math.abs(dollars) >= 1000 ? 0 : 2,
  })
}

/** "Week of Sep 29" for a `YYYY-MM-DD` Monday, in UTC. */
export const weekLabel = (iso: string): string =>
  `Week of ${new Date(`${iso}T00:00:00.000Z`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  })}`
